import * as d3 from 'd3'
import Chart from '../../ultils/Chart'
import emptyThumb from '../../assets/images/emptydata.svg'
import './styles.css'
import d3tip from '../../ultils/d3-tip'
import {
  countYAxisTick,
  FUNDAMENTAL_TOGGLE_COLOR,
} from '../../ultils/chart-d3-utils'

class LineChart extends Chart {
  init({ data, relations, setSelection, lineKeys, inModal, chartId }) {
    let vis = this
    vis.data = data
    vis.lineKeys = lineKeys
    vis.relations = relations
    vis.setSelection = setSelection
    vis.inModal = inModal
    vis.chartId = chartId

    vis.parseTime = d3.timeParse('%d/%m/%Y')
    vis.formatTime = d3.timeFormat('%d/%m/%Y')
    vis.bisectDate = d3.bisector(d => d.date).left

    vis.g
      .append('path')
      .attr('class', 'line1')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.g
      .append('path')
      .attr('class', 'line2')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.x = d3.scaleTime()
    vis.y = d3.scaleLinear()

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(inModal ? 8 : 2)
      .tickSizeOuter(0)
    vis.yAxisCall = d3
      .axisLeft()
      .ticks(inModal ? 6 : 4)
      .tickSizeOuter(0)

    vis.xAxis = vis.g
      .append('g')
      .attr('class', 'x axisLineChart')
      .attr('transform', `translate(0, ${vis.HEIGHT})`)
    vis.yAxis = vis.g.append('g').attr('class', 'y axisLineChart')

    vis.focus = vis.g
      .append('g')
      .attr('class', 'focus')
      .style('display', 'none')

    vis.focus
      .append('line')
      .attr('class', 'x-hover-line hover-line')
      .attr('y1', 0)
      .attr('y2', vis.HEIGHT)

    vis.yLegend = vis.svg
      .append('text')
      .attr('class', inModal ? 'axis-label label-in-modal' : 'axis-label')
      .attr('text-anchor', 'end')
      .attr('x', inModal ? 110 : 50)
      .attr('y', 10)
    vis.createToolTip()
    vis.update({ data, lineKeys })
  }

  formatNumber(number) {
    return !isNaN(number) ? Number(number).toFixed(2) : number
  }

  createToolTip() {
    const vis = this
    const inModal = vis.inModal ? '-inModal' : '-notInModal'
    d3.selectAll(`.line-chart${vis.chartId}${inModal}`).remove()
    vis.tip = d3tip()
      .attr('class', `d3-tip-line-chart line-chart${vis.chartId}${inModal}`)
      .html((e, d) => vis.generateTooltipHtml(d))
    vis.g.call(vis.tip)
  }

  generateTooltipHtml(data) {
    const vis = this
    let returnDiv = `<div class="tip-content line-chart-tip-content">`
    if (data.hasOwnProperty(vis.lineKeys[0]) && vis.showCompanyLine) {
      returnDiv += `<div class="row">
                        <div class="line-chart-row-title">
                          <span class="dot" style="background-color: #3073c3;"></span> Standard Deviation from Company Norm: 
                        </div>
                        <div class="line-chart-row-content">${vis.formatNumber(
                          data[vis.lineKeys[0]],
                        )}</div>
                      </div>`
    }
    if (data.hasOwnProperty(vis.lineKeys[1]) && vis.showPeerLine) {
      returnDiv += `<div class="row">
                        <div class="line-chart-row-title">
                          <span class="dot" style="background-color: #bf8264;"></span> Peer Group: 
                        </div>
                      <div class="line-chart-row-content">${vis.formatNumber(
                        data[vis.lineKeys[1]],
                      )}</div>
                      </div>`
    }
    returnDiv += `</div></div></div>`
    return returnDiv
  }

  mousemove(e) {
    let vis = this
    if (vis.data.length > 0) {
      const x0 = vis.x.invert(d3.pointer(e)[0] - vis.MARGIN.LEFT)
      const i = vis.bisectDate(vis.data, x0, 1, vis.data.length - 1)
      const d0 = vis.data[i - 1]
      const d1 = vis.data[i]
      const d = x0 - d0.date > d1.date - x0 ? d1 : d0

      vis.focus
        .select('.x-hover-line')
        .attr('x1', vis.x(d.date))
        .attr('x2', vis.x(d.date))

      vis.focus.selectAll('circle').remove()
      if (d.hasOwnProperty(vis.lineKeys[0]) && vis.showCompanyLine) {
        vis.focus
          .append('circle')
          .attr('class', 'companyHistory')
          .attr('r', 4)
          .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.COMPANY_HISTORY)

        vis.focus
          .select('.companyHistory')
          .attr('cx', vis.x(d.date))
          .attr('cy', vis.y(d[vis.lineKeys[0]]))
      }

      if (d.hasOwnProperty(vis.lineKeys[1]) && vis.showPeerLine) {
        vis.focus
          .append('circle')
          .attr('class', 'peerGroup')
          .attr('r', 4)
          .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.PEER_GROUP)
        vis.focus
          .select('.peerGroup')
          .attr('cx', vis.x(d.date))
          .attr('cy', vis.y(d[vis.lineKeys[1]]))
      }

      vis.tip.show(e, d, 10, -d3.pointer(e)[1] - 2)
    }
  }

  update({ data, lineKeys }) {
    let vis = this
    vis.data = data || vis.data
    vis.lineKeys = lineKeys || vis.lineKeys
    vis.t = d3.transition().duration(1000)
    vis.svg.selectAll('image').remove()

    if (vis.data.length <= 1) {
      // treat 1 record as empty state to clear xAxis & yAxis value
      vis.data = []
      vis.svg
        .append('svg:image')
        .attr('xlink:href', emptyThumb)
        .attr('x', vis.WIDTH / 2 - 77 + vis.MARGIN.LEFT)
        .attr('y', vis.HEIGHT / 2 - 37 + vis.MARGIN.TOP)
        .attr('width', '155')
        .attr('height', '73')
      vis.yLegend.text('')
    } else {
      vis.yLegend.text(vis.inModal ? 'Standard Deviation' : '')
    }

    vis.x.range([0, vis.WIDTH]).domain(d3.extent(vis.data, d => d.date))
    vis.y
      .range([vis.HEIGHT, 0])
      .domain([
        d3.min([
          d3.min(vis.data, d => Number(d[vis.lineKeys[0]])),
          d3.min(vis.data, d => Number(d[vis.lineKeys[1]])),
        ]) - 1,
        d3.max([
          d3.max(vis.data, d => Number(d[vis.lineKeys[0]])),
          d3.max(vis.data, d => Number(d[vis.lineKeys[1]])),
        ]) + 1,
      ])

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(countYAxisTick(vis.data))
      .tickSizeOuter(0)

    vis.xAxisCall.scale(vis.x)
    vis.xAxis.transition(vis.t).call(vis.xAxisCall)
    vis.yAxisCall.scale(vis.y)
    vis.yAxis.transition(vis.t).call(vis.yAxisCall)

    vis.showCompanyLine = vis.data.reduce(
      (curr, acc) =>
        curr || !(acc[vis.lineKeys[0]] === null || acc[vis.lineKeys[0]] === 0),
      false,
    )

    vis.showPeerLine = vis.data.reduce(
      (curr, acc) =>
        curr || !(acc[vis.lineKeys[1]] === null || acc[vis.lineKeys[1]] === 0),
      false,
    )

    vis.companyLine = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(d => !isNaN(d[vis.lineKeys[0]]))
      .x(d => vis.x(d.date))
      .y(d => vis.y(d[vis.lineKeys[0]]))

    vis.g
      .select('.line1')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.COMPANY_HISTORY)
      .transition(vis.t)
      .attr('d', vis.showCompanyLine ? vis.companyLine(vis.data) : null)

    vis.peerLine = d3
      .line()
      .curve(d3.curveCatmullRom)
      .defined(d => !isNaN(d[vis.lineKeys[1]]))
      .x(d => vis.x(d.date))
      .y(d => vis.y(d[vis.lineKeys[1]]))

    vis.g
      .select('.line2')
      .attr('stroke-width', '2px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.PEER_GROUP)
      .attr('opacity', 0.9)
      .transition(vis.t)
      .attr('d', vis.showPeerLine ? vis.peerLine(vis.data) : null)

    vis.svg
      .on('mousemove', e => vis.mousemove(e))
      .on('mouseover', () => {
        // vis.tip.hide()
        vis.focus.style('display', null)
        !vis.inModal && vis.setSelection(vis.relations)
      })
      .on('mouseout', () => {
        vis.tip.hide()
        vis.focus.style('display', 'none')
        !vis.inModal && vis.setSelection(null)
      })
  }
}

export default LineChart
